import { Controller } from "@hotwired/stimulus"
import { collectPageSpans, findValueMatches } from "../lib/pdf_review/matcher"

// Finds one value on the PDF and marks where it is. The attachment modal gets
// it when a reviewer clicks a marked value on a task (value-locate controller),
// so "is this really a 0?" is answered by the document itself.
//
// pdf.js renders only the pages in view, so the page is found from the PDF's
// own text first, scrolled to, and marked once its text layer has rendered.
// When the value is not in the document's text (a scan without a text layer,
// or the value is from the other source), it marks nothing. The status line is
// for screen readers; the ring is what a sighted reader sees.
export default class extends Controller {
  static values = { query: String }

  connect() {
    this.targetPage = null
    this.scrolled = false
    this.onReady = () => this.locate()
    this.onRendered = (event) => { if (event.detail?.pageNumber === this.targetPage) this.mark() }
    this.element.addEventListener("pdf-viewer:pages-ready", this.onReady)
    this.element.addEventListener("pdf-viewer:page-rendered", this.onRendered)
    this.status = document.createElement("div")
    this.status.className = "pdf-find-status"
    this.status.setAttribute("role", "status")
    this.element.prepend(this.status)
    this.say(`Looking for ${this.queryValue}…`, "busy")
  }

  disconnect() {
    this.element.removeEventListener("pdf-viewer:pages-ready", this.onReady)
    this.element.removeEventListener("pdf-viewer:page-rendered", this.onRendered)
    this.status?.remove()
  }

  async locate() {
    const viewer = this.application.getControllerForElementAndIdentifier(this.element, "pdf-viewer")
    const pdf = viewer?.pdfDocument
    if (!pdf) return
    const wanted = this.compact(this.queryValue)
    if (!wanted) return
    for (let number = 1; number <= pdf.numPages; number++) {
      const content = await (await pdf.getPage(number)).getTextContent()
      if (this.compact(content.items.map((item) => item.str).join("")).includes(wanted)) {
        this.targetPage = number
        break
      }
    }
    if (!this.targetPage) {
      this.say(`${this.queryValue} is not in the document's text.`, "missing")
      return
    }
    viewer.pdfViewer.currentPageNumber = this.targetPage
    this.mark()
  }

  // Called again whenever the target page renders again (pdf.js re-renders a
  // page when its scale changes), so the ring is redrawn, in percent of the
  // page, where the text is now.
  mark() {
    const page = this.element.querySelector(`.page[data-page-number="${this.targetPage}"]`)
    if (!page?.querySelector(".textLayer span")) return
    page.querySelectorAll(".pdf-find-mark").forEach((old) => old.remove())
    const result = findValueMatches(this.queryValue, new Map([[this.targetPage - 1, collectPageSpans(page)]]), "find")
    if (!result.matches.length) {
      this.say(`${this.queryValue} is on page ${this.targetPage}.`, "found")
      return
    }
    page.style.position = "relative"
    const pageRect = page.getBoundingClientRect()
    const percent = (value, size) => `${(value / size) * 100}%`
    let first = null
    result.matches.forEach((match) => {
      const rects = match.spans.map((span) => span.el.getBoundingClientRect())
      const left = Math.min(...rects.map((r) => r.left)) - pageRect.left - 3
      const top = Math.min(...rects.map((r) => r.top)) - pageRect.top - 2
      const right = Math.max(...rects.map((r) => r.right)) - pageRect.left + 3
      const bottom = Math.max(...rects.map((r) => r.bottom)) - pageRect.top + 2
      const box = document.createElement("div")
      box.className = "pdf-find-mark"
      box.style.left = percent(left, pageRect.width)
      box.style.top = percent(top, pageRect.height)
      box.style.width = percent(right - left, pageRect.width)
      box.style.height = percent(bottom - top, pageRect.height)
      page.appendChild(box)
      first ||= box
    })
    if (!this.scrolled) {
      first.scrollIntoView({ block: "center" })
      this.scrolled = true
    }
    const count = result.matches.length
    this.say(`${this.queryValue}: page ${this.targetPage}${count > 1 ? `, ${count} places` : ""}`, "found")
  }

  // Case, spaces, and punctuation never decide whether the value is there.
  compact(text) {
    return (text || "").normalize("NFKC").toUpperCase().replace(/[^\p{L}\p{N}]/gu, "")
  }

  say(text, tone) {
    this.status.textContent = text
    this.status.dataset.tone = tone
  }
}
